import React, { useEffect, useState, useCallback } from "react";

// Self-contained offline indicator. Lives inside the React tree so it never
// unmounts the app shell — Provider, PersistGate, QueryClient and Router all
// stay alive while it's visible. Auto-dismisses on the browser's `online`
// event; manual Retry actively probes a static asset (cache-busted) so a
// captive-portal or stale `navigator.onLine` flag doesn't leave the user
// stuck behind the overlay.
function OfflineOverlay() {
  const initialOffline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  const [isOffline, setIsOffline] = useState(initialOffline);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const handleRetry = useCallback(async () => {
    setChecking(true);
    try {
      // /favicon.ico is small, served by nginx (no API hit), and cache-busted
      // here so we read live network state rather than the SW cache.
      const res = await fetch("/favicon.ico?_=" + Date.now(), {
        cache: "no-store",
        method: "GET",
      });
      if (res && res.ok) {
        setIsOffline(false);
      }
    } catch {
      /* probe failed — keep the overlay up */
    } finally {
      setChecking(false);
    }
  }, []);

  if (!isOffline) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 20, 20, 0.55)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          maxWidth: 420,
          width: "100%",
          padding: "24px 22px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#FFF8E1",
            color: "#B26A00",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          !
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 18, color: "#1a1a1a" }}>
          You&apos;re offline
        </h3>
        <p
          style={{
            margin: "0 0 18px",
            color: "#555",
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          Your internet connection is unavailable. OfferYard will resume
          automatically as soon as you&apos;re back online.
        </p>
        <button
          type="button"
          onClick={handleRetry}
          disabled={checking}
          style={{
            padding: "10px 22px",
            borderRadius: 6,
            border: "1px solid #d4a900",
            background: checking ? "#FFE082" : "#FFC107",
            color: "#1a1a1a",
            cursor: checking ? "wait" : "pointer",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {checking ? "Checking…" : "Try again"}
        </button>
      </div>
    </div>
  );
}

export default OfflineOverlay;
